/**
 * MemberSite LINE LIFF 登入流程的狀態機（純 reducer，用法類似 Redux reducer）。
 *
 * 用法：呼叫端（外殼元件 LiffLoginPage.jsx）呼叫 reduce(state, event, payload) 取得 nextState，
 * state 長相是 { stage, flags, effect?, secondaryEffect? }：
 * - stage：目前走到流程的哪一步（見 STAGES）。
 * - effect / secondaryEffect：這一步「應該」要做的外部動作名稱（見 EFFECTS），reducer 只負責宣告，
 *   實際呼叫 API、liff SDK、更新畫面等動作都由外殼看到這個名稱後自己執行。
 *
 * 設計原則（釘死，勿破壞）：
 * - 這個檔案必須是純函式，不能有任何 side effect，也不能 import @line/liff、react、history、localUser
 *   ——這樣才能在不啟動瀏覽器環境的情況下單元測試整個流程。
 * - 所有「外部世界發生了什麼」（session 查詢結果、LIFF 是否 ready、後端 API 回應…）一律靠外殼組成
 *   event 餵進來，reducer 本身完全不主動去查。
 * - 遇到不合法的 (stage, event) 組合（例如已經 DONE 了卻又收到別的 event）一律回傳原 state（no-op），
 *   並帶上 code: 'IGNORED_<event>' 供除錯；絕對不能 throw——因為 React 18 StrictMode 下同一個
 *   effect 可能被重複呼叫一次，若 throw 會直接讓畫面崩潰。
 */

// Stages
export const STAGES = {
    INIT: 'INIT',
    SESSION_CHECK: 'SESSION_CHECK',
    LIFF_LOGIN: 'LIFF_LOGIN',
    FRIENDSHIP: 'FRIENDSHIP',
    BIND_AUTH: 'BIND_AUTH',
    BIND_FORM: 'BIND_FORM',
    DONE: 'DONE',
    UNAUTH: 'UNAUTH',
    SIGNED_OUT: 'SIGNED_OUT',
    ERROR: 'ERROR',
};

// Events
export const EVENTS = {
    SESSION_CHECK_REQUESTED: 'SESSION_CHECK_REQUESTED',
    SESSION_HAS_LOGIN: 'SESSION_HAS_LOGIN',
    SESSION_NO_LOGIN: 'SESSION_NO_LOGIN',
    LIFF_READY: 'LIFF_READY',
    LIFF_INIT_ERROR: 'LIFF_INIT_ERROR',
    LIFF_ALREADY_LOGGED_IN: 'LIFF_ALREADY_LOGGED_IN',
    LIFF_LOGIN_REQUIRED: 'LIFF_LOGIN_REQUIRED',
    FRIENDSHIP_FRIEND: 'FRIENDSHIP_FRIEND',
    FRIENDSHIP_NOT_FRIEND: 'FRIENDSHIP_NOT_FRIEND',
    FRIENDSHIP_ERROR: 'FRIENDSHIP_ERROR',
    BIND_AUTH_RESULT: 'BIND_AUTH_RESULT',
    BIND_FORM_SUBMITTED: 'BIND_FORM_SUBMITTED',
    RETRY: 'RETRY',
    FLOW_ERROR: 'FLOW_ERROR',
};

// Effects（reducer 只宣告，不執行）
export const EFFECTS = {
    CHECK_SESSION: 'EFFECT_CHECK_SESSION',
    LOGIN_ACTION: 'EFFECT_LOGIN_ACTION',
    START_LIFF_FLOW: 'EFFECT_START_LIFF_FLOW',
    LIFF_LOGIN: 'EFFECT_LIFF_LOGIN',
    GET_FRIENDSHIP: 'EFFECT_GET_FRIENDSHIP',
    LIFF_AUTH: 'EFFECT_LIFF_AUTH',
    SHOW_OFFICE_ACCOUNT: 'EFFECT_SHOW_OFFICE_ACCOUNT',
    SHOW_ERROR: 'EFFECT_SHOW_ERROR',
    LOGOUT_LIFF: 'EFFECT_LOGOUT_LIFF',
    SHOW_UNAUTH: 'EFFECT_SHOW_UNAUTH',
    SHOW_BIND_FORM: 'EFFECT_SHOW_BIND_FORM',
    SHOW_SIGNOUT_SNACKBAR: 'EFFECT_SHOW_SIGNOUT_SNACKBAR',
    SHOW_LOGIN_ERROR: 'EFFECT_SHOW_LOGIN_ERROR',
    RESET_UI: 'EFFECT_RESET_UI',
};

/**
 * 初始 state。SESSION_CHECK 階段要「等兩件事都做完」才能往下走：
 * 本站 session 有沒有登入（sessionResolved）、LIFF SDK 有沒有 init 完成（liffReady）。
 * 這兩件事是各自非同步、順序不定，所以先各用一個旗標記下「這件事完成了沒」，兩個都 true 才轉下一階段。
 */
export const INIT = Object.freeze({
    stage: STAGES.INIT,
    flags: Object.freeze({ liffReady: false, sessionResolved: false }),
});

// 註：錯誤訊息的實際文字（給使用者看的字句）由外殼決定，reducer 只帶錯誤 code，不管文案。

/**
 * 空值判斷。這裡自己刻一份、不 import @Util/utils 的 isEmpty，是因為要維持本檔「零 import 副作用模組」的規則。
 * 判斷規則跟原本流程一致：null / undefined / 空字串都算空。
 * @param {*} value
 * @returns {boolean}
 */
const isBlank = (value) => value === null || value === undefined || value === '';

/**
 * 判斷後端回傳的 result 該往哪個方向走。result 的四個欄位意義：
 * - isLiffSignOut：後端說這個 LINE 帳號已被解除綁定／登出，要把前端也登出。
 * - isRedirectToUnAuthUri：後端說這個人沒有使用本站的權限。
 * - displayNo / displayName：兩者都有值才代表已經完成綁定、可以正常登入。
 * 判斷順序刻意固定（先 sign-out，再 unauth，最後才看有沒有 displayNo/displayName）：
 * 因為同一筆 result 理論上可能同時符合多個條件，順序錯了會導向錯的畫面。
 * @param {object} result
 * @returns {'SIGN_OUT'|'UNAUTH'|'LOGIN_SUCCESS'|'NEEDS_BIND_FORM'}
 */
const classifyResult = (result) => {
    const {
        isLiffSignOut = false,
        isRedirectToUnAuthUri = false,
        displayNo = '',
        displayName = '',
    } = result || {};

    if (isLiffSignOut) return 'SIGN_OUT';
    if (isRedirectToUnAuthUri) return 'UNAUTH';
    if (!isBlank(displayNo) && !isBlank(displayName)) return 'LOGIN_SUCCESS';
    return 'NEEDS_BIND_FORM';
};

/** 收到不合法的 (stage, event) 組合時統一從這裡回傳：原封不動的 state + 診斷用 code。
 * 不丟例外，因為 React 18 StrictMode 下同一個 effect 可能被重複呼叫，若 throw 會直接讓畫面崩潰。 */
const ignore = (state, event) => ({ ...state, code: `IGNORED_${event}`, effect: undefined });

/**
 * 純 reducer：reduce(state, event, payload?) → nextState。
 * state = { stage, flags, code?, effect? }。
 * @param {object} state 目前 state（至少含 stage、flags）
 * @param {string} event event 名
 * @param {object} [payload] event 附帶資料
 * @returns {object} nextState
 */
export const reduce = (state, event, payload = {}) => {
    // 防禦：外殼理應永遠傳入合法 state；若缺，退回初始（不 throw）
    const current = state && state.stage ? state : INIT;
    const { stage, flags = INIT.flags } = current;

    switch (stage) {
        case STAGES.INIT: {
            if (event === EVENTS.SESSION_CHECK_REQUESTED) {
                return {
                    stage: STAGES.SESSION_CHECK,
                    flags,
                    effect: EFFECTS.CHECK_SESSION,
                };
            }
            return ignore(current, event);
        }

        case STAGES.SESSION_CHECK: {
            // 本站已經有登入態：不用等 LIFF ready，直接視為完成，跳去 DONE 導頁
            if (event === EVENTS.SESSION_HAS_LOGIN) {
                return {
                    stage: STAGES.DONE,
                    flags,
                    effect: EFFECTS.LOGIN_ACTION,
                };
            }

            /**
             * 本站尚未登入：記下「session 查詢完成」旗標；若 LIFF 那邊也已經 ready 了（兩件事都完成），
             * 才轉去 LIFF_LOGIN 開始走 LIFF 登入流程，否則留在原地等另一個旗標
             */
            if (event === EVENTS.SESSION_NO_LOGIN) {
                const nextFlags = { ...flags, sessionResolved: true };
                if (nextFlags.liffReady) {
                    return {
                        stage: STAGES.LIFF_LOGIN,
                        flags: nextFlags,
                        effect: EFFECTS.START_LIFF_FLOW,
                    };
                }
                return { stage: STAGES.SESSION_CHECK, flags: nextFlags };
            }

            // 對稱邏輯：LIFF ready 先到，記旗標；若 session 那邊也已經查完了才轉 LIFF_LOGIN
            if (event === EVENTS.LIFF_READY) {
                const nextFlags = { ...flags, liffReady: true };
                if (nextFlags.sessionResolved) {
                    return {
                        stage: STAGES.LIFF_LOGIN,
                        flags: nextFlags,
                        effect: EFFECTS.START_LIFF_FLOW,
                    };
                }
                return { stage: STAGES.SESSION_CHECK, flags: nextFlags };
            }

            if (event === EVENTS.LIFF_INIT_ERROR) {
                return {
                    stage: STAGES.ERROR,
                    flags,
                    code: 'LIFF_INIT_ERROR',
                    effect: EFFECTS.SHOW_ERROR,
                };
            }

            if (event === EVENTS.FLOW_ERROR) {
                return { stage: STAGES.ERROR, flags, code: 'FLOW_ERROR', effect: EFFECTS.SHOW_ERROR };
            }

            return ignore(current, event);
        }

        case STAGES.LIFF_LOGIN: {
            if (event === EVENTS.LIFF_ALREADY_LOGGED_IN) {
                return { stage: STAGES.FRIENDSHIP, flags, effect: EFFECTS.GET_FRIENDSHIP };
            }
            if (event === EVENTS.LIFF_LOGIN_REQUIRED) {
                /**
                 * 使用者用外部瀏覽器打開、還沒登入 LINE：effect 會導去 LINE 的外部登入頁面，
                 * 離開本頁面了，所以 stage 留在原地即可（導轉回來後會是全新的一次 mount，不會延續這個 state）
                 */
                return { stage: STAGES.LIFF_LOGIN, flags, effect: EFFECTS.LIFF_LOGIN };
            }
            if (event === EVENTS.FLOW_ERROR) {
                return { stage: STAGES.ERROR, flags, code: 'FLOW_ERROR', effect: EFFECTS.SHOW_ERROR };
            }
            return ignore(current, event);
        }

        // FRIENDSHIP：查詢使用者是否已加官方帳號好友，依結果決定走自動綁定（BIND_AUTH）還是顯示加好友頁（BIND_FORM）
        case STAGES.FRIENDSHIP: {
            if (event === EVENTS.FRIENDSHIP_FRIEND) {
                return { stage: STAGES.BIND_AUTH, flags, effect: EFFECTS.LIFF_AUTH };
            }
            if (event === EVENTS.FRIENDSHIP_NOT_FRIEND) {
                return { stage: STAGES.BIND_FORM, flags, effect: EFFECTS.SHOW_OFFICE_ACCOUNT };
            }
            if (event === EVENTS.FRIENDSHIP_ERROR) {
                return { stage: STAGES.ERROR, flags, code: 'FRIENDSHIP_ERROR', effect: EFFECTS.SHOW_ERROR };
            }
            if (event === EVENTS.FLOW_ERROR) {
                return { stage: STAGES.ERROR, flags, code: 'FLOW_ERROR', effect: EFFECTS.SHOW_ERROR };
            }
            return ignore(current, event);
        }

        // BIND_AUTH：使用者已加官方帳號好友，系統自動拿 LINE profile 呼叫後端做綁定驗證（不需使用者手動輸入）
        case STAGES.BIND_AUTH: {
            if (event === EVENTS.BIND_AUTH_RESULT) {
                const kind = classifyResult(payload.result);
                // 這是「自動驗證」路徑，使用者沒有主動送出任何表單，所以直接登出、不用跳 snackbar 特別提示
                if (kind === 'SIGN_OUT') {
                    return { stage: STAGES.SIGNED_OUT, flags, code: 'AUTH_SIGN_OUT', effect: EFFECTS.LOGOUT_LIFF };
                }
                if (kind === 'UNAUTH') {
                    return { stage: STAGES.UNAUTH, flags, effect: EFFECTS.SHOW_UNAUTH };
                }
                if (kind === 'LOGIN_SUCCESS') {
                    return { stage: STAGES.DONE, flags, effect: EFFECTS.LOGIN_ACTION };
                }
                // 自動驗證沒能拿到完整會員資料：轉去 BIND_FORM 讓使用者手動輸入補齊
                return { stage: STAGES.BIND_FORM, flags, effect: EFFECTS.SHOW_BIND_FORM };
            }
            if (event === EVENTS.FLOW_ERROR) {
                return { stage: STAGES.ERROR, flags, code: 'FLOW_ERROR', effect: EFFECTS.SHOW_ERROR };
            }
            return ignore(current, event);
        }

        // BIND_FORM：使用者手動輸入會員編號／驗證碼並送出表單（例如未加好友、或 BIND_AUTH 判斷需要補資料時會落到這一站）
        case STAGES.BIND_FORM: {
            if (event === EVENTS.BIND_FORM_SUBMITTED) {
                const kind = classifyResult(payload.result);
                /**
                 * 這是使用者「主動送出表單」後才發現帳號被登出，所以要跳 snackbar 明確告知，
                 * 不能像 BIND_AUTH 那樣悄悄登出——使用者剛剛才按了按鈕，會覺得莫名其妙
                 */
                if (kind === 'SIGN_OUT') {
                    return {
                        stage: STAGES.SIGNED_OUT,
                        flags,
                        code: 'LOGIN_SIGN_OUT',
                        effect: EFFECTS.SHOW_SIGNOUT_SNACKBAR,
                        /**
                         * secondaryEffect：這次轉換需要外殼依序做兩件事——先跳 snackbar（primary effect），
                         * 顯示完之後才執行真正的登出（LOGOUT_LIFF）。放在 secondaryEffect 而非直接兩個都塞
                         * primary，是因為外殼會先跑完 primary effect、再跑 secondaryEffect，順序才會對。
                         */
                        secondaryEffect: EFFECTS.LOGOUT_LIFF,
                    };
                }
                if (kind === 'UNAUTH') {
                    return { stage: STAGES.UNAUTH, flags, effect: EFFECTS.SHOW_UNAUTH };
                }
                if (kind === 'LOGIN_SUCCESS') {
                    return { stage: STAGES.DONE, flags, effect: EFFECTS.LOGIN_ACTION };
                }
                /**
                 * 理論上表單送出後不該還是「需要補資料」，這是防禦性的邊界情況：
                 * 忠實比照舊版 loginAction 失敗時的行為（跳錯誤 snackbar + 登出），不當機
                 */
                return { stage: STAGES.BIND_FORM, flags, effect: EFFECTS.SHOW_LOGIN_ERROR };
            }
            /**
             * 停在「未加好友」畫面按下「再次驗證」：重新走一次 LIFF 登入流程判斷，
             * 但不重新呼叫 liff.init()（LIFF SDK 全站只 init 一次，見 LiffProvider.jsx）
             */
            if (event === EVENTS.RETRY) {
                return {
                    stage: STAGES.LIFF_LOGIN,
                    flags,
                    effect: EFFECTS.RESET_UI,
                    secondaryEffect: EFFECTS.START_LIFF_FLOW,
                };
            }
            if (event === EVENTS.FLOW_ERROR) {
                return { stage: STAGES.ERROR, flags, code: 'FLOW_ERROR', effect: EFFECTS.SHOW_ERROR };
            }
            return ignore(current, event);
        }

        case STAGES.ERROR: {
            // 跟 BIND_FORM 的 RETRY 是同一種邏輯：回 LIFF_LOGIN 重跑流程判斷，不重新 init
            if (event === EVENTS.RETRY) {
                return {
                    stage: STAGES.LIFF_LOGIN,
                    flags,
                    effect: EFFECTS.RESET_UI,
                    secondaryEffect: EFFECTS.START_LIFF_FLOW,
                };
            }
            return ignore(current, event);
        }

        // 終態：流程已經結束（登入成功 / 沒有權限 / 已登出），之後不管收到什麼 event 都不再變化
        case STAGES.DONE:
        case STAGES.UNAUTH:
        case STAGES.SIGNED_OUT:
            return ignore(current, event);

        default:
            return ignore(current, event);
    }
};

export default reduce;
